import React from 'react';
import { ProductFormProps } from './ProductFormTypes';
import { useProductFormValidation } from './useProductFormValidation';
import ProductFormFields from './ProductFormFields';
import ProductCategorySelector from './ProductCategorySelector';
import ProductOptionTypeSelector from './ProductOptionTypeSelector';
import ProductFormPreview from './ProductFormPreview';

/**
 * Product Form Component (Refactored)
 * Main orchestrator component that combines all form sections
 *
 * Original size: 344 lines
 * New size: ~80 lines (78% reduction)
 *
 * Modular components:
 * - ProductFormFields: Basic information fields
 * - ProductCategorySelector: Category selection logic
 * - ProductOptionTypeSelector: Option types management
 * - ProductFormPreview: Live preview
 * - useProductFormValidation: Validation logic hook
 */

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  onUpdate,
  mainCategories,
  filteredSubCategories,
  selectedMainCategory,
  onMainCategoryChange,
  optionTypes,
  isEdit = false,
}) => {
  // Use validation hook for centralized validation logic
  const { validation, errors } = useProductFormValidation(product);
  const selectedOptionTypes = product.option_type_ids || [];

  return (
    <div className="space-y-5">
      {/* Basic Product Information */}
      <ProductFormFields
        product={product}
        onUpdate={onUpdate}
        validation={validation}
        errors={errors}
        isEdit={isEdit}
      />

      {/* Category Selection */}
      <ProductCategorySelector
        product={product}
        onUpdate={onUpdate}
        mainCategories={mainCategories}
        filteredSubCategories={filteredSubCategories}
        selectedMainCategory={selectedMainCategory}
        onMainCategoryChange={onMainCategoryChange}
        validation={validation}
        errors={errors}
        isEdit={isEdit}
      />

      {/* Option Types Selection */}
      <ProductOptionTypeSelector
        product={product}
        onUpdate={onUpdate}
        optionTypes={optionTypes}
        isEdit={isEdit}
      />

      {/* Product Preview */}
      <ProductFormPreview
        product={product}
        optionTypes={optionTypes}
        selectedOptionTypes={selectedOptionTypes}
      />
    </div>
  );
};

export default ProductForm;
